import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import { JobPostingFilter } from './JobPostingFilter';
import type { IJobPostingFilterProps } from '../interfaces';

interface IJobFilterPanelProps extends IJobPostingFilterProps {
  showFilters: boolean;
  onToggleFilters: () => void;
  activeFilterCount: number;
}

export const JobFilterPanel = ({
  showFilters,
  onToggleFilters,
  activeFilterCount,
  ...filterProps
}: IJobFilterPanelProps) => {
  const { t } = useTranslation(NS.jobPortal);

  return (
    <>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <Paper sx={{ position: 'sticky', top: 20, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar variant="rounded" sx={{ bgcolor: 'primary.50', color: 'primary.main', width: 36, height: 36 }}>
                <FilterListIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={800}>
                  {t(JOB_PORTAL_KEYS.listing.filters)}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Narrow down open roles
                </Typography>
              </Box>
            </Stack>
          </Box>
          <JobPostingFilter {...filterProps} />
        </Paper>
      </Box>

      <Box sx={{ display: { lg: 'none' } }}>
        <Paper
          role="button"
          tabIndex={0}
          aria-expanded={showFilters}
          onClick={onToggleFilters}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onToggleFilters();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            cursor: 'pointer',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar variant="rounded" sx={{ bgcolor: 'primary.50', color: 'primary.main', width: 32, height: 32 }}>
              <FilterListIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={800} display="block">
                {t(JOB_PORTAL_KEYS.listing.filtersAndSearch)}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {activeFilterCount} active filters
              </Typography>
            </Box>
          </Stack>
          <ExpandMoreIcon
            color="action"
            sx={{
              transform: showFilters ? 'rotate(180deg)' : 'none',
              transition: 'transform 200ms ease',
              minWidth: 44,
              minHeight: 44,
            }}
          />
        </Paper>

        {showFilters && (
          <Paper sx={{ mt: 1.5, overflow: 'hidden' }}>
            <JobPostingFilter {...filterProps} />
          </Paper>
        )}
      </Box>
    </>
  );
};
